/**
 * Edição de produto — a tela que faltava.
 *
 * O formulário é o MESMO do cadastro (`FormularioProduto.tsx`); o que esta tela
 * acrescenta é o que só existe quando o produto já existe:
 *
 *  1. As fotos vão direto para o servidor. Aqui não há fila de espera: o
 *     produto existe, então enviar, remover e trocar a capa são chamadas que
 *     acontecem na hora — e a resposta traz a lista já renumerada.
 *  2. Mexer na grade tem CONSEQUÊNCIA, e ela é mostrada depois de salvar:
 *     variação nova nasce com código de barras, variação que sai é DESATIVADA
 *     e não apagada quando tem saldo ou movimento. Os textos vêm do servidor —
 *     quem sabe o que a grade sofreu é quem a reconciliou.
 *  3. O interruptor ativo/inativo. Inativar tira da vitrine e não apaga nada.
 *
 * O CÓDIGO não é editável. Ele é identidade de negócio, está impresso nas
 * etiquetas já emitidas e compõe a URL desta página (CONSTITUICAO.md §2).
 *
 * Custo e mínimo vêm em branco quando as variações divergem entre si: mandar de
 * volta "o que aparecia na tela" igualaria todas, apagando a repreçagem que a
 * loja fez depois de um recebimento.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Botao, LinkBotao } from '../../componentes/Botao'
import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { formatar } from '../../plataforma/dinheiro'
import { useSessao } from '../../plataforma/contexto-sessao'
import {
  carregarFicha,
  definirCapa,
  editarProduto,
  enviarFoto,
  motivoParaRecusarFoto,
  removerFoto,
  urlDaFoto,
  MAX_FOTOS,
  type Cor,
  type Ficha,
  type Foto,
  type ResultadoEdicao,
} from '../../plataforma/produtos'
import { LayoutApp } from '../app/LayoutApp'
import { BlocoDeFotos, FormularioProduto, type FotoNaTela } from './FormularioProduto'
import { valoresVazios, PALETA, type ValoresProduto } from './valores'

export function TelaEdicaoProduto() {
  const { codigo = '' } = useParams<{ codigo: string }>()
  const { podeVer } = useSessao()
  const veCusto = podeVer('cost.read')
  const navegar = useNavigate()

  const [ficha, setFicha] = useState<Ficha | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erroDeCarga, setErroDeCarga] = useState('')

  const [ativo, setAtivo] = useState(true)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState('')
  const [porCampo, setPorCampo] = useState<Record<string, string>>({})
  const [salvo, setSalvo] = useState<ResultadoEdicao | null>(null)

  const [fotos, setFotos] = useState<Foto[]>([])
  const [erroDeFoto, setErroDeFoto] = useState('')
  const [mexendoNaFoto, setMexendoNaFoto] = useState(false)

  useEffect(() => {
    const controle = new AbortController()
    setCarregando(true)

    carregarFicha(codigo, '', controle.signal)
      .then((dados) => {
        setFicha(dados)
        setAtivo(dados.ativo)
        setFotos(dados.fotos)
        setErroDeCarga('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErroDeCarga(
          causa instanceof ErroApi ? causa.message : 'Não foi possível carregar o produto.',
        )
      })
      .finally(() => setCarregando(false))

    return () => controle.abort()
  }, [codigo])

  const enviar = async (valores: ValoresProduto) => {
    setEnviando(true)
    setErro('')
    setPorCampo({})
    try {
      const saida = await editarProduto(codigo, {
        nome: valores.nome.trim(),
        categoria: valores.categoria,
        marca: valores.marca.trim(),
        preco: valores.preco.trim(),
        // Campo vazio não vai no pedido: ausente é "mantenha o de cada
        // variação". Mandar string vazia seria pedir para zerar.
        custo: veCusto && valores.custo.trim() !== '' ? valores.custo.trim() : undefined,
        estoque_minimo: valores.minimo.trim() === '' ? undefined : Number(valores.minimo),
        cores: valores.cores.map(
          (nomeDaCor) => coresConhecidas(ficha).find((cor) => cor.nome === nomeDaCor) ?? ({ nome: nomeDaCor } as Cor),
        ),
        tamanhos: valores.tamanhos,
        ativo,
      })
      setSalvo(saida)
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setErro('Não foi possível salvar o produto.')
        return
      }
      setErro(causa.message)
      setPorCampo(causa.campos)
    } finally {
      setEnviando(false)
    }
  }

  /** Envolve as três operações de foto: mesmo erro, mesmo estado de ocupado. */
  const mexerNaFoto = useCallback(
    async (acao: () => Promise<{ fotos: Foto[] }>) => {
      setMexendoNaFoto(true)
      setErroDeFoto('')
      try {
        const lista = await acao()
        setFotos(lista.fotos)
      } catch (causa: unknown) {
        setErroDeFoto(
          causa instanceof ErroApi ? causa.message : 'Não foi possível alterar as fotos.',
        )
      } finally {
        setMexendoNaFoto(false)
      }
    },
    [],
  )

  const escolherFotos = async (arquivos: File[]) => {
    setErroDeFoto('')

    const recusas: string[] = []
    let cabem = MAX_FOTOS - fotos.length

    for (const arquivo of arquivos) {
      if (cabem <= 0) {
        recusas.push(`O limite é de ${MAX_FOTOS} fotos por produto.`)
        break
      }
      const motivo = motivoParaRecusarFoto(arquivo)
      if (motivo) {
        recusas.push(motivo)
        continue
      }
      cabem--
      // Em SÉRIE: a ordem no banco é a ordem de chegada, e disparar tudo em
      // paralelo entregaria a capa em qualquer posição.
      await mexerNaFoto(() => enviarFoto(codigo, arquivo))
    }

    if (recusas.length > 0) setErroDeFoto(recusas.join(' '))
  }

  if (carregando) {
    return (
      <LayoutApp titulo="Editar produto" subtitulo="Carregando…">
        <div className="h-[420px] w-full animate-pulse rounded-sistema bg-superficie" />
      </LayoutApp>
    )
  }

  if (erroDeCarga || !ficha) {
    return (
      <LayoutApp titulo="Editar produto" subtitulo={codigo}>
        <div className="flex w-full flex-col gap-4">
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            {erroDeCarga || 'Produto não encontrado.'}
          </div>
          <LinkBotao para="/app/produtos" variante="secundario" icone="arrow_back">
          Voltar ao catálogo
        </LinkBotao>
        </div>
      </LayoutApp>
    )
  }

  if (salvo) {
    return (
      <LayoutApp
        titulo="Produto salvo"
        subtitulo={`${salvo.codigo} · ${salvo.skus} ${salvo.skus === 1 ? 'SKU ativo' : 'SKUs ativos'}`}
      >
        <ResumoDaEdicao
          salvo={salvo}
          aoContinuar={() => {
            setSalvo(null)
            // A ficha é recarregada porque a grade mudou: sem isso o
            // formulário reabriria com as variações antigas.
            void carregarFicha(codigo, '').then((dados) => {
              setFicha(dados)
              setAtivo(dados.ativo)
              setFotos(dados.fotos)
            })
          }}
        />
      </LayoutApp>
    )
  }

  const daTela: FotoNaTela[] = fotos.map((foto) => ({
    chave: `${foto.ordem}-${foto.nome_arquivo}`,
    url: urlDaFoto(codigo, foto.ordem),
    nome: foto.nome_arquivo,
    tamanho: foto.bytes,
    capa: foto.capa,
  }))

  return (
    <LayoutApp
      trilha={[
        { rotulo: 'Produtos', para: '/app/produtos' },
        { rotulo: ficha.nome },
      ]}
      voltarPara="auto"
      titulo={`Editar ${ficha.nome}`}
      subtitulo={`${ficha.codigo} · ${ficha.skus} ${ficha.skus === 1 ? 'SKU' : 'SKUs'} na grade`}
    >
      <FormularioProduto
        modo="edicao"
        inicial={valoresDaFicha(ficha, veCusto)}
        veCusto={veCusto}
        enviando={enviando}
        erro={erro}
        porCampo={porCampo}
        coresExtras={ficha.cores.map((cor) => ({ nome: cor.cor, hex: cor.hex }))}
        tamanhosExtras={ficha.tamanhos}
        placeholderCusto={custoUniforme(ficha) === null ? 'varia por variação' : undefined}
        placeholderMinimo={minimoUniforme(ficha) === null ? 'varia' : undefined}
        aoEnviar={(valores) => void enviar(valores)}
        aoCancelar={() => navegar('/app/produtos')}
        rotuloDoBotao={() => (enviando ? 'Salvando…' : 'Salvar alterações')}
        blocoDeFotos={
          <BlocoDeFotos
            fotos={daTela}
            erro={erroDeFoto}
            ocupado={mexendoNaFoto}
            aoEscolher={(arquivos) => void escolherFotos(arquivos)}
            aoRemover={(foto) => {
              const alvo = fotos.find((item) => `${item.ordem}-${item.nome_arquivo}` === foto.chave)
              if (alvo) void mexerNaFoto(() => removerFoto(codigo, alvo.ordem))
            }}
            aoMarcarCapa={(foto) => {
              const alvo = fotos.find((item) => `${item.ordem}-${item.nome_arquivo}` === foto.chave)
              if (alvo) void mexerNaFoto(() => definirCapa(codigo, alvo.ordem))
            }}
          />
        }
        avisoDoRail={<AvisoDaGrade ficha={ficha} />}
        extrasDoRail={<InterruptorDeAtivo ativo={ativo} aoTrocar={setAtivo} />}
      />
    </LayoutApp>
  )
}

/* ------------------------------------------------------------- pedaços */

/**
 * O interruptor ativo/inativo.
 *
 * `role="switch"` com `aria-checked`, e não uma caixa de seleção estilizada: é
 * o controle do catálogo de componentes, e é o que faz o leitor de tela
 * anunciar "ligado/desligado" em vez de "marcado".
 */
function InterruptorDeAtivo({
  ativo,
  aoTrocar,
}: {
  ativo: boolean
  aoTrocar: (valor: boolean) => void
}) {
  return (
    <div className="mt-4 rounded-sistema border border-borda bg-fundo px-3.5 py-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[12.5px] font-semibold text-texto">Produto ativo</span>
        {/* Sem `outline-none`: o foco é o contorno global de tokens.css, e
            trocá-lo por um anel local é como a borda de foco ficou meses sem
            aparecer neste repositório — utilitário vence base. */}
        <button
          type="button"
          role="switch"
          aria-checked={ativo}
          aria-label="Produto ativo"
          onClick={() => aoTrocar(!ativo)}
          className={`relative h-[22px] w-[40px] shrink-0 rounded-full transition-colors hover:brightness-95 ${
            ativo ? 'bg-marca' : 'bg-inativo'
          }`}
        >
          <span
            aria-hidden
            className={`absolute top-[3px] h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-all ${
              ativo ? 'left-[21px]' : 'left-[3px]'
            }`}
          />
        </button>
      </div>
      <p className="mt-1.5 text-[11.5px] leading-[1.45] text-texto-fraco">
        {ativo
          ? 'Aparece no catálogo e no PDV.'
          : 'Sai do catálogo e do PDV. O estoque continua registrado e o produto volta pelo filtro “Mostrar inativos”.'}
      </p>
    </div>
  )
}

/** Aviso do rail: mexer na grade tem consequência, e ela é dita ANTES. */
function AvisoDaGrade({ ficha }: { ficha: Ficha }) {
  return (
    <div className="mt-3 flex gap-2.5 rounded-sistema border border-borda bg-fundo px-3.5 py-3">
      <Icone nome="info" tamanho={19} className="text-texto-fraco" />
      <p className="text-[12.5px] leading-[1.55] text-texto-suave">
        Acrescentar cor ou tamanho cria SKU novo, com código de barras próprio. Tirar da grade{' '}
        <strong className="font-semibold text-texto">desativa</strong> a variação — o que já tem
        saldo ou movimento continua no histórico do estoque.
        {ficha.pecas > 0 && ` Hoje há ${ficha.pecas} ${ficha.pecas === 1 ? 'peça' : 'peças'} na grade.`}
      </p>
    </div>
  )
}

/** O que a edição fez, dito pelo servidor. */
function ResumoDaEdicao({
  salvo,
  aoContinuar,
}: {
  salvo: ResultadoEdicao
  aoContinuar: () => void
}) {
  const comHistorico = salvo.variacoes_desativadas.filter((v) => v.com_historico)

  return (
    <div className="flex w-full flex-col gap-4">
      <div
        role="status"
        className="flex gap-3 rounded-sistema border border-ok-suave bg-ok-suave px-4 py-3.5"
      >
        <Icone nome="check_circle" tamanho={20} className="text-ok" />
        <p className="text-[13px] leading-[1.55] text-ok">
          <strong className="font-semibold">{salvo.nome}</strong> salvo com {salvo.skus}{' '}
          {salvo.skus === 1 ? 'variação ativa' : 'variações ativas'} — {salvo.cores}{' '}
          {salvo.cores === 1 ? 'cor' : 'cores'} × {salvo.tamanhos}{' '}
          {salvo.tamanhos === 1 ? 'tamanho' : 'tamanhos'}.
        </p>
      </div>

      {salvo.avisos.length > 0 && (
        <div className="flex gap-3 rounded-sistema border border-atencao-suave bg-atencao-fundo px-4 py-3.5">
          <Icone nome="warning" tamanho={20} className="text-atencao-texto" />
          <ul className="list-none text-[13px] leading-[1.6] text-atencao-texto">
            {salvo.avisos.map((aviso) => (
              <li key={aviso}>{aviso}</li>
            ))}
          </ul>
        </div>
      )}

      {salvo.variacoes_criadas.length > 0 && (
        <TabelaDeVariacoes
          titulo="Variações criadas"
          dica="Cada uma nasceu com código de barras próprio — reimprima as etiquetas."
          linhas={salvo.variacoes_criadas.map((v) => ({
            chave: v.codigo_barras,
            cor: v.cor,
            hex: v.hex,
            tamanho: v.tamanho,
            detalhe: v.codigo_barras,
          }))}
          rotuloDoDetalhe="Código de barras"
        />
      )}

      {comHistorico.length > 0 && (
        <TabelaDeVariacoes
          titulo="Variações desativadas com histórico"
          dica="Elas saíram da grade e continuam no banco: o extrato de estoque aponta para elas."
          linhas={comHistorico.map((v) => ({
            chave: `${v.cor}-${v.tamanho}`,
            cor: v.cor,
            tamanho: v.tamanho,
            detalhe: `${v.saldo} ${v.saldo === 1 ? 'peça' : 'peças'} em estoque`,
          }))}
          rotuloDoDetalhe="Situação"
        />
      )}

      <div className="flex flex-wrap gap-2.5">
        <Botao variante="secundario" icone="edit_note" onClick={aoContinuar}>
          Continuar editando
        </Botao>
        <LinkBotao para="/app/produtos">Ver no catálogo</LinkBotao>
      </div>
    </div>
  )
}

function TabelaDeVariacoes({
  titulo,
  dica,
  linhas,
  rotuloDoDetalhe,
}: {
  titulo: string
  dica: string
  linhas: { chave: string; cor: string; hex?: string; tamanho: string; detalhe: string }[]
  rotuloDoDetalhe: string
}) {
  return (
    <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
      <div className="border-b border-borda px-4 py-3">
        <p className="text-sm font-semibold text-tinta">{titulo}</p>
        <p className="text-[12.5px] text-texto-suave">{dica}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] border-collapse">
          <caption className="sr-only">{titulo}</caption>
          <thead>
            <tr className="bg-fundo">
              {['Cor', 'Tamanho', rotuloDoDetalhe].map((coluna) => (
                <th
                  key={coluna}
                  scope="col"
                  className="border-b border-borda px-3 py-2.5 text-left text-[12.5px] font-semibold text-texto-suave"
                >
                  {coluna}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha) => (
              <tr key={linha.chave}>
                <td className="border-b border-divisoria px-3 py-2 text-[13px] text-tinta">
                  <span className="flex items-center gap-2">
                    {linha.hex && (
                      <span
                        aria-hidden
                        style={{ background: linha.hex }}
                        className="h-3.5 w-3.5 shrink-0 rounded-full border border-[rgba(0,0,0,0.12)]"
                      />
                    )}
                    {linha.cor}
                  </span>
                </td>
                <td className="tabular border-b border-divisoria px-3 py-2 text-[13px] font-semibold text-tinta">
                  {linha.tamanho}
                </td>
                <td className="tabular border-b border-divisoria px-3 py-2 text-[13px] text-texto-suave">
                  {linha.detalhe}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------- utilidades */

function coresConhecidas(ficha: Ficha | null): Cor[] {
  const daFicha = (ficha?.cores ?? []).map((cor) => ({ nome: cor.cor, hex: cor.hex }))
  const nomes = new Set(daFicha.map((cor) => cor.nome.toLowerCase()))
  return [...daFicha, ...PALETA.filter((cor) => !nomes.has(cor.nome.toLowerCase()))]
}

/** Preenche o formulário a partir da ficha. */
function valoresDaFicha(ficha: Ficha, veCusto: boolean): ValoresProduto {
  const custo = veCusto ? custoUniforme(ficha) : null
  const minimo = minimoUniforme(ficha)

  return {
    ...valoresVazios(),
    nome: ficha.nome,
    codigo: ficha.codigo,
    categoria: ficha.categoria,
    marca: ficha.marca ?? '',
    preco: formatar(ficha.preco_centavos),
    // Vazio quando as variações divergem: preencher com uma delas e salvar
    // igualaria todas em silêncio.
    custo: custo === null ? '' : formatar(custo),
    minimo: minimo === null ? '' : String(minimo),
    tamanhos: ficha.tamanhos,
    cores: ficha.cores.map((cor) => cor.cor),
  }
}

/** O custo comum a todas as células, ou `null` se elas divergem. */
function custoUniforme(ficha: Ficha): number | null {
  return uniforme(celulas(ficha).map((celula) => celula.custo_centavos))
}

/** O mesmo para o estoque mínimo. */
function minimoUniforme(ficha: Ficha): number | null {
  return uniforme(celulas(ficha).map((celula) => celula.estoque_minimo))
}

function celulas(ficha: Ficha) {
  return ficha.cores.flatMap((cor) => cor.celulas.filter((celula) => celula.existe))
}

function uniforme(valores: (number | undefined)[]): number | null {
  const presentes = valores.filter((valor): valor is number => valor !== undefined)
  const primeiro = presentes[0]
  if (primeiro === undefined) return null
  return presentes.every((valor) => valor === primeiro) ? primeiro : null
}
